import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, User, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
    const [mode, setMode] = useState('login'); // 'login' or 'register'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [registrationSent, setRegistrationSent] = useState(false);
    
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const userData = await login(email, password);
            toast.success('Connexion reussie');
            switch (userData.role) {
                case 'commune': navigate('/commune'); break;
                case 'gymnase_admin':
                case 'gymnase_chef_poste': navigate('/persons'); break;
                case 'moyen': navigate('/vehicles'); break;
                default: navigate('/dashboard');
            }
        } catch (err) {
            const message = err.response?.data?.detail || 'Une erreur est survenue';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await axios.post(`${API}/auth/request-registration`, { email, password, name });
            setRegistrationSent(true);
            toast.success('Demande envoyee');
        } catch (err) {
            const message = err.response?.data?.detail || 'Une erreur est survenue';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    if (registrationSent) {
        return (
            <div className="login-container">
                <div className="login-bg" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1557683316-973673baf926?w=1920')` }} />
                <div className="login-card slide-in">
                    <div className="flex flex-col items-center mb-6">
                        <img src="/civis-logo-slogan.png" alt="CIVIS+" className="h-40 sm:h-48 mb-3" />
                    </div>
                    <div className="text-center space-y-4" data-testid="registration-success">
                        <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
                            <CheckCircle2 className="w-8 h-8 text-green-400" />
                        </div>
                        <h2 className="text-xl font-semibold text-white">Demande envoyee</h2>
                        <p className="text-zinc-400 text-sm leading-relaxed">
                            Votre demande d'inscription a bien ete envoyee.<br />
                            Un administrateur vous attribuera un role pour acceder a la plateforme.
                        </p>
                        <Button
                            onClick={() => { setRegistrationSent(false); setMode('login'); setEmail(''); setPassword(''); setName(''); }}
                            className="mt-4 tactical-btn"
                            data-testid="back-to-login-btn"
                        >
                            Retour a la connexion
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            <div className="login-bg" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1557683316-973673baf926?w=1920')` }} />
            <div className="login-card slide-in">
                <div className="flex flex-col items-center mb-8">
                    <img src="/civis-logo-slogan.png" alt="CIVIS+" className="h-40 sm:h-48 mb-3" data-testid="login-logo" />
                    <p className="text-sm text-zinc-400">Plateforme de gestion de crise v2.0</p>
                </div>

                <h2 className="text-xl font-semibold text-white mb-6">
                    {mode === 'login' ? 'Connexion' : 'Demande d\'inscription'}
                </h2>

                {error && (
                    <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="auth-error">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                    </div>
                )}

                <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="space-y-4">
                    {mode === 'register' && (
                        <div className="form-group">
                            <Label htmlFor="name" className="form-label">Nom complet</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                                <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                                    placeholder="Jean Dupont" className="pl-10 tactical-input" required
                                    data-testid="register-name-input" />
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <Label htmlFor="email" className="form-label">Email</Label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                placeholder="email@example.com" className="pl-10 tactical-input" required
                                data-testid="login-email-input" />
                        </div>
                    </div>

                    <div className="form-group">
                        <Label htmlFor="password" className="form-label">Mot de passe</Label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                placeholder="******" className="pl-10 tactical-input" required
                                data-testid="login-password-input" />
                        </div>
                    </div>

                    {mode === 'register' && (
                        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                            <p className="text-xs text-blue-400">
                                Votre demande sera examinee par un administrateur qui vous attribuera un role d'acces.
                            </p>
                        </div>
                    )}

                    <Button type="submit" className="w-full tactical-btn" disabled={loading} data-testid="login-submit-btn">
                        {loading ? 'Chargement...' : (mode === 'login' ? 'Se connecter' : 'Envoyer la demande')}
                    </Button>
                </form>

                <div className="mt-6 text-center">
                    <button type="button" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
                        className="text-blue-400 hover:text-blue-300 text-sm transition-colors" data-testid="toggle-auth-mode-btn">
                        {mode === 'login' ? "Pas de compte ? Demander un acces" : 'Deja un compte ? Se connecter'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
